import { CardDesign, CardDesignSlot, Product, ProductPageDesign, ProductSummary } from 'types/product';
import { defaultCardFieldLabel, resolveCardFieldValue } from 'utils/cardField';

export const PRODUCT_PAGE_SLOT_IDS = [
  'gallery',
  'overline',
  'title',
  'meta',
  'price',
  'description',
  'specs',
  'badges',
] as const;

export const defaultProductPageDesign = (): ProductPageDesign => ({
  template: 'classic',
  image: { aspect: '1/1', objectFit: 'contain', bg: 'muted' },
  slots: [
    { id: 'gallery', visible: true },
    { id: 'overline', field: 'brand', visible: true },
    { id: 'title', field: 'name', visible: true },
    { id: 'meta', visible: true, fields: [{ field: 'sku', labelMode: 'none' }] },
    { id: 'price', field: 'price', visible: true },
    { id: 'description', visible: true },
    { id: 'specs', visible: true },
    { id: 'badges', visible: false, flagFields: [] },
  ],
  actions: { primary: 'addToCart', secondary: null, requireAuthForCart: true },
});

export const getProductPageSlot = (
  design: ProductPageDesign | null | undefined,
  id: string
): CardDesignSlot | undefined => design?.slots?.find((s) => s.id === id);

export const fromCardDesign = (card: CardDesign | null | undefined): ProductPageDesign => {
  const fallback = defaultProductPageDesign();
  if (!card) return fallback;
  const template =
    card.template === 'mediaFocus'
      ? 'galleryFocus'
      : card.template === 'commerce' || card.template === 'compact'
        ? card.template
        : 'classic';
  const slot = (id: string) => card.slots?.find((s) => s.id === id);
  return {
    template,
    image: card.image ?? fallback.image,
    slots: fallback.slots.map((preset) => {
      if (preset.id === 'gallery' || preset.id === 'description' || preset.id === 'specs') return preset;
      if (preset.id === 'overline') return { id: 'overline', field: 'brand', visible: true };
      if (preset.id === 'title') return { id: 'title', field: 'name', visible: true };
      if (preset.id === 'price') return { id: 'price', field: 'price', visible: true };
      if (preset.id === 'badges') return { id: 'badges', visible: false, flagFields: [] };
      const existing = slot(preset.id);
      return existing ? { ...preset, ...existing, id: preset.id } : preset;
    }),
    actions: {
      primary: 'addToCart',
      secondary: null,
      requireAuthForCart: card.actions?.requireAuthForCart !== false,
    },
  };
};

export const resolveProductPageMeta = (
  product: ProductSummary | Product,
  design: ProductPageDesign | null | undefined
): { label: string; value: string; labelMode: 'inline' | 'none' }[] => {
  const metaSlot = getProductPageSlot(design, 'meta');
  if (!metaSlot?.visible || !metaSlot.fields?.length || !product) return [];
  return metaSlot.fields
    .map((f) => {
      const value = resolveCardFieldValue(product, f.field);
      if (!value) return null;
      const specLabel =
        f.field.startsWith('spec:')
          ? product.specs?.find((s) => s.key === f.field.slice(5))?.label
          : undefined;
      return {
        label: f.label || specLabel || defaultCardFieldLabel(f.field) || 'Dato',
        value,
        labelMode: (f.labelMode || 'none') as 'inline' | 'none',
      };
    })
    .filter(Boolean) as { label: string; value: string; labelMode: 'inline' | 'none' }[];
};

/** Muestra el nombre largo si el slot está activo y el API envió texto. */
export const shouldShowProductPageDescription = (
  product: Pick<Product, 'description'>,
  design: ProductPageDesign | null | undefined
): boolean => {
  if (getProductPageSlot(design, 'description')?.visible === false) return false;
  return Boolean(product.description?.trim());
};
